import { Link } from "react-router-dom";
import { ArrowRight, Droplets, PenLine, Sun, Wrench } from "lucide-react";
import { collectionFor, badgeOn, cap } from "../lib/useCaseCollections.mjs";

const ICONS = { droplets: Droplets, pen: PenLine, sun: Sun, wrench: Wrench };

export function BadgeIcon({ badge, on, className = "w-4 h-4" }) {
  const Icon = ICONS[badge.icon] || Sun;
  return <Icon className={`${className} ${on ? badge.onClass : "text-slate-300"}`} aria-label={on ? badge.yes : badge.no} />;
}

export function UseCaseGrid({ collection, compact = false }) {
  const c = collection;
  return (
    <div className={`grid ${compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 md:grid-cols-4"} gap-3`} data-testid={`${c.key}-use-case-grid`}>
      {c.useCases.map((u) => (
        <Link key={u.slug} to={`${c.basePath}/${u.slug}`} className="bg-white border border-slate-200 hover:border-kolt-orange p-4 group transition-colors" data-testid={`${c.key}-use-case-${u.slug}`}>
          <div className="font-bold text-kolt-slate-dark">{cap(u.keyword)}</div>
          {!compact && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{u.h1}</p>}
          <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-kolt-orange inline-flex items-center gap-1">Guide & picks <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></div>
        </Link>
      ))}
    </div>
  );
}

export function StockTable({ collection }) {
  const c = collection;
  return (
    <div className="overflow-x-auto border border-slate-200 bg-white" data-testid={`${c.key}-stock-table`}>
      <table className="w-full text-sm text-left">
        <thead className="bg-slate-50 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2">{c.tableHead}</th><th className="px-3 py-2">Finish</th>
            {c.badges.map((b) => <th key={b.key} className="px-3 py-2">{b.yes}</th>)}
            <th className="px-3 py-2">Best for</th>
          </tr>
        </thead>
        <tbody>
          {c.stocks.map((s) => (
            <tr key={s.slug} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-3 py-2"><Link to={c.productPath(s.slug)} className="font-bold text-kolt-slate-dark hover:text-kolt-orange" data-testid={`stock-row-${s.slug}`}>{s.name}</Link></td>
              <td className="px-3 py-2 text-slate-600">{s.finish}</td>
              {c.badges.map((b) => <td key={b.key} className="px-3 py-2"><BadgeIcon badge={b} on={badgeOn(s, b)} /></td>)}
              <td className="px-3 py-2 text-slate-600">{s.best}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HubExtras({ basePath }) {
  const c = collectionFor(basePath);
  if (!c) return null;
  return (
    <section className="mt-10 space-y-8" data-testid={`${c.key}-hub-extras`}>
      <div>
        <div className="overline mb-1">{c.gridOverline}</div>
        <h2 className="font-display font-black text-2xl text-kolt-slate-dark tracking-tight mb-4">{c.gridTitle}</h2>
        <UseCaseGrid collection={c} />
      </div>
      <div>
        <div className="overline mb-1">{c.tableOverline}</div>
        <h2 className="font-display font-black text-2xl text-kolt-slate-dark tracking-tight mb-4">{c.tableTitle}</h2>
        <StockTable collection={c} />
      </div>
    </section>
  );
}
